import { useMutation } from '@tanstack/react-query';
import { Skeleton, Form, Input, Button } from 'antd';
import { CRX_SUPPORT_EMAIL } from 'app/config';
import { BaseButton } from 'components/button/button-base';
import { ErrorDisplay } from 'components/ErrorDisplay';
import { Text } from 'components/mvp-typography';
import { QAPanelTemplate } from 'features/patient/assessment/assessment-controller/_components/questionnaire-qa-panel';
import { APIIntegrationImPACT } from 'lib/api/firebase/apis/api-integration-impact';
import useSearchParams from 'lib/hooks/use-searchParams';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';
import { PropsWithChildren, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { paths } from 'routes';
import { useToOnboardingQuery } from './useToOnboardingQuery';

export function TranslateImPACTLeadToOnboardingRequest() {
  // Collect tokens
  const [params] = useSearchParams();
  const passportID =
    /* `token` is the legacy field that ImPACT provides until they update their system */ params.get(
      'token'
    ) ??
    params.get('passportID') ??
    '';
  const locationCode = params.get('location') ?? '';
  const urlImpactOrganisationId = params.get('impactOrganisationID') ?? '';

  const [domain, setDomain] = useState<string | undefined>(undefined);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  const q = useToOnboardingQuery({
    passportID,
    impactOrganisationId: urlImpactOrganisationId || undefined,
    domain,
    locationCode
  });

  const handleEmailSubmit = (values: { email: string }) => {
    const enteredEmail = values.email.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(enteredEmail)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    setEmailError(null);
    const extractedDomain = enteredEmail.split('@')[1];
    setDomain(extractedDomain);
  };

  // If impactOrganisationId is missing and domain is not yet extracted, show the email form
  if (!urlImpactOrganisationId && !domain) {
    return (
      <Root>
        <div style={{ maxWidth: 400, margin: '40px auto', padding: '0 16px' }}>
          <Text
            style={{
              fontSize: '20px',
              fontWeight: 'bold',
              display: 'block',
              marginBottom: '16px'
            }}
          >
            Identify Your Organisation
          </Text>
          <Text style={{ display: 'block', marginBottom: '24px' }}>
            Please enter your email address to help us identify your
            organisation.
          </Text>
          <Form onFinish={handleEmailSubmit} layout="vertical">
            <Form.Item
              label="Email Address"
              name="email"
              rules={[
                { required: true, message: 'Please input your email address!' },
                {
                  type: 'email',
                  message: 'Please enter a valid email address!'
                }
              ]}
              validateStatus={emailError ? 'error' : undefined}
              help={emailError}
            >
              <Input
                placeholder="you@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" block>
                Submit
              </Button>
            </Form.Item>
          </Form>
        </div>
      </Root>
    );
  }

  if (q.isLoading) {
    return (
      <Root>
        <Text>Validating request...</Text>
        <Skeleton />
      </Root>
    );
  }

  if (q.isError) {
    return (
      <Root>
        <ErrorDisplay
          title="Failed to validate the request"
          description={`Please contact ${CRX_SUPPORT_EMAIL}`}
          error={resolveError(q.error)}
        />
      </Root>
    );
  }

  return (
    <Root>
      <Redirect
        to={{
          pathname: paths.onboarding.landing(),
          search: new URLSearchParams({
            token: q.data?.onboardingToken
          }).toString()
        }}
      />
      <h1>Redirect to {q.data?.onboardingToken}</h1>
    </Root>
  );
}

function useGetFakePilotRedirectParamsMutation() {
  return useMutation({
    mutationFn: () => APIIntegrationImPACT.getFakePilotRedirectParams({})
  });
}
function Root(props: PropsWithChildren<object>) {
  const mut = useGetFakePilotRedirectParamsMutation();
  const [, updateParams] = useSearchParams();
  return (
    <div>
      <QAPanelTemplate>
        <BaseButton
          onClick={async () => {
            const result = await mut.mutateAsync();
            updateParams({ token: result.passportID });
          }}
        >
          Get new params
        </BaseButton>
      </QAPanelTemplate>
      {props.children}
    </div>
  );
}
